import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { UserPlus, Users, Calendar, FileText, LogOut, GraduationCap, Building2, ClipboardList, LayoutGrid, BarChart3, BookOpen, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import logo from "@/assets/skylink-logo.png";
import Footer from "@/components/Footer";
import { InstructorProfileDialog } from "@/components/InstructorProfileDialog";
import { AlertsWidget } from "@/components/AlertsWidget";
import { NotificationBell } from "@/components/NotificationBell";
import { InstructorSelfAttendance } from "@/components/InstructorSelfAttendance";
import { GlobalSearch } from "@/components/GlobalSearch";
import { BranchContextCard } from "@/components/BranchContextCard";
import { DashboardSection } from "@/components/DashboardSection";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { MyClassesWidget } from "@/components/MyClassesWidget";
import { ClassOverviewWidget } from "@/components/ClassOverviewWidget";
import { AbsenceFollowUpWidget } from "@/components/AbsenceFollowUpWidget";
import { PendingItemsTrackerWidget } from "@/components/PendingItemsTrackerWidget";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ThemeToggle } from "@/components/ThemeToggle";
import { YearSelector } from "@/components/YearSelector";
import { useYear } from "@/contexts/YearContext";

const Dashboard = () => {
  const navigate = useNavigate();
  const { data: dashboardData, isLoading, error } = useDashboardData();
  const { selectedYear } = useYear();
  const [showInstructorDialog, setShowInstructorDialog] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);

  // Enable keyboard shortcuts
  useKeyboardShortcuts({ enabled: !isLoading && !!dashboardData });

  useEffect(() => {
    // Only redirect to auth if explicitly not loading AND no data
    // This prevents redirect during initial load
    if (!isLoading && dashboardData === null) {
      // Double check session before redirecting
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!session) {
          navigate("/auth");
        }
      });
    }
  }, [isLoading, dashboardData, navigate]);

  useEffect(() => {
    if (dashboardData?.isInstructor && !dashboardData.hasInstructorRecord) {
      setShowInstructorDialog(true);
    }
  }, [dashboardData]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Only redirect on explicit sign out
      if (event === 'SIGNED_OUT' || !session) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast.success("Logged out successfully");
    navigate("/auth");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card sticky top-0 z-50">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <Skeleton className="h-8 sm:h-10 w-20 sm:w-24" />
              <Skeleton className="h-6 w-32" />
            </div>
            <Skeleton className="h-9 w-20" />
          </div>
        </header>
        <main className="container mx-auto px-4 py-4 sm:py-8">
          <div className="mb-6 sm:mb-8">
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-5 w-64" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="stagger-item" style={{ animationDelay: `${i * 50}ms` }}>
                <CardHeader className="p-4 sm:p-6">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-full" />
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (!dashboardData) {
    return null;
  }

  const { userName, userEmail, userId, isAdmin, isSuperAdmin, isInstructor, branches } = dashboardData;

  // Simplified navigation sections
  const studentSection = {
    title: "Students",
    cards: [
      { title: "Add Student", description: "Register new student", icon: UserPlus, path: "/students/add" },
      { title: "Progress Tracker", description: "View all students", icon: GraduationCap, path: "/students" },
    ].filter(() => isAdmin),
  };

  const classesSection = {
    title: "Classes",
    cards: [
      { title: "Subject Classes", description: "Manage classes", icon: BookOpen, path: "/subject-classes" },
    ],
  };

  const attendanceSection = {
    title: "Attendance",
    cards: [
      { title: "Student Attendance", description: "Mark attendance", icon: Calendar, path: "/attendance/student" },
      ...(isAdmin ? [{ title: "Instructor Attendance", description: "Mark instructor attendance", icon: ClipboardList, path: "/attendance/instructor" }] : []),
      { title: "Calendar View", description: "View in calendar", icon: Calendar, path: "/attendance/calendar" },
    ],
  };

  const slotsSection = {
    title: "Scheduling",
    cards: [
      { title: "Book Slot", description: "Schedule slots", icon: Calendar, path: "/flight-slots" },
      { title: "View Calendar", description: "Check availability", icon: LayoutGrid, path: "/view-slots" },
    ],
  };

  const setupSection = {
    title: "Setup",
    cards: [
      { title: "Add Subject", description: "Add subjects", icon: FileText, path: "/subjects/add" },
      { title: "Add Batch", description: "Create batch", icon: Users, path: "/batches/add" },
    ].filter(() => isAdmin),
  };

  const adminSection = {
    title: "Administration",
    cards: [
      { title: "Reports & Analytics", description: "View insights", icon: BarChart3, path: "/reports" },
      { title: "Master Summary", description: "Complete overview", icon: FileText, path: "/summary" },
      { title: "Manage Accounts", description: "User accounts", icon: Users, path: "/manage-accounts" },
      { title: "Activity Logs", description: "Admin actions", icon: FileText, path: "/admin-logs" },
      ...(isSuperAdmin ? [{ title: "Branch Management", description: "Manage branches", icon: Building2, path: "/branch-management" }] : []),
    ].filter(() => isAdmin),
  };

  // Instructor-only view (not admin)
  const isInstructorOnly = isInstructor && !isAdmin;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0 flex-shrink">
            <img src={logo} alt="Skylink" className="h-8 sm:h-10 flex-shrink-0" />
            <h1 className="text-sm sm:text-xl font-bold truncate hidden xs:block">Management Portal</h1>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <GlobalSearch />
            <YearSelector />
            <p className="text-xs sm:text-sm text-muted-foreground hidden lg:block truncate max-w-[120px]">Welcome, {userName}</p>
            <ThemeToggle />
            <NotificationBell />
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-4 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2">Dashboard</h2>
          <p className="text-sm sm:text-base text-muted-foreground">
            {isInstructorOnly ? "Manage your classes and track attendance" : "Quick access to all management features"} · Viewing year <span className="font-medium text-foreground">{selectedYear}</span>
          </p>
        </div>

        {/* Branch Context Card - Admin only */}
        {isAdmin && branches.length > 0 && (
          <div className="mb-6">
            <BranchContextCard branches={branches} isSuperAdmin={isSuperAdmin} />
          </div>
        )}

        {/* Instructor Self-Attendance - Instructor only */}
        {isInstructor && !isAdmin && (
          <div className="mb-6">
            <InstructorSelfAttendance userId={userId} />
          </div>
        )}

        {/* Main Content Grid */}
        {isInstructorOnly ? (
          /* Instructor Dashboard - Simplified */
          <div className="space-y-6">
            <MyClassesWidget />
            <DashboardSection title={classesSection.title} cards={classesSection.cards} />
            <DashboardSection title={slotsSection.title} cards={slotsSection.cards} />
          </div>
        ) : (
          /* Admin/Manager Dashboard */
          <>
            {/* Class Overview and Absence Follow-up Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <ClassOverviewWidget />
              <AbsenceFollowUpWidget />
            </div>

            {/* Pending Items Tracker for Managers */}
            <div className="mb-6">
              <PendingItemsTrackerWidget />
            </div>

            {/* Collapsible Alerts */}
            <div className="mb-6">
              <Collapsible open={alertsOpen} onOpenChange={setAlertsOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" className="w-full justify-between">
                    <span className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Alerts & Reminders
                    </span>
                    {alertsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-4">
                  <AlertsWidget />
                </CollapsibleContent>
              </Collapsible>
            </div>

            {/* Navigation Sections */}
            {isAdmin && <DashboardSection title={studentSection.title} cards={studentSection.cards} />}
            <DashboardSection title={classesSection.title} cards={classesSection.cards} />
            <DashboardSection title={attendanceSection.title} cards={attendanceSection.cards} />
            <DashboardSection title={slotsSection.title} cards={slotsSection.cards} />
            {isAdmin && <DashboardSection title={setupSection.title} cards={setupSection.cards} />}
            {isAdmin && <DashboardSection title={adminSection.title} cards={adminSection.cards} />}
          </>
        )}
      </main>
      <Footer />
      
      <InstructorProfileDialog
        open={showInstructorDialog}
        onOpenChange={setShowInstructorDialog}
        userId={userId}
        userEmail={userEmail || ""}
        onComplete={() => {
          setShowInstructorDialog(false);
          toast.success("Profile completed! Please refresh the page.");
          setTimeout(() => window.location.reload(), 2000);
        }}
      />
    </div>
  );
};

export default Dashboard;
